import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, RECORD_TYPES, STATUSES, PRIORITIES, ASSIGNEES, SECTIONS } from "@/lib/api";
import { cn } from "@/lib/utils";

const empty = {
  section: "Adliye",
  record_type: "Gelen Evrak",
  title: "",
  description: "",
  due_date: "",
  assignee: "Umut Sağlam",
  priority: "Normal",
  status: "Yapılacak",
  doc_date: "",
  drive_file_id: null,
  drive_link: "",
  target_folder_link: "",
  deadline_needs_check: false,
};

export const TaskFormDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const editing = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, ...(initial || {}) });
      setMoreOpen(Boolean(initial?.id));
    }
  }, [open, initial]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Başlık zorunludur.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/tasks/${initial.id}`, {
          section: form.section, title: form.title, description: form.description,
          due_date: form.due_date || null, record_type: form.record_type,
          assignee: form.assignee, priority: form.priority,
          doc_date: form.doc_date || null, target_folder_link: form.target_folder_link || null,
          deadline_needs_check: form.deadline_needs_check,
        });
        toast.success("Kayıt güncellendi.");
      } else {
        await api.post("/tasks", {
          ...form,
          due_date: form.due_date || null,
          doc_date: form.doc_date || null,
          drive_link: form.drive_link || null,
          target_folder_link: form.target_folder_link || null,
        });
        toast.success("Kayıt oluşturuldu.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">
            {editing ? "Kaydı Düzenle" : "Yeni Kayıt"}
          </DialogTitle>
          <DialogDescription>Bölüm, başlık, yapılacak iş ve son tarih yeterlidir.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Bölüm</Label>
            <Select
              value={form.section}
              onValueChange={(v) => setForm((f) => ({ ...f, section: v, record_type: RECORD_TYPES[v][0] }))}
            >
              <SelectTrigger data-testid="form-section"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Başlık</Label>
            <Input
              data-testid="form-title"
              value={form.title}
              onChange={(e) => set("title")(e.target.value)}
              placeholder="Örn. Ankara 5. İcra Müdürlüğü – ödeme emri"
            />
          </div>

          <div className="space-y-2">
            <Label>Yapılacak İş</Label>
            <Textarea
              data-testid="form-description"
              rows={3}
              value={form.description || ""}
              onChange={(e) => set("description")(e.target.value)}
              placeholder="Örn. İtiraz dilekçesi hazırlanacak"
            />
          </div>

          <div className="space-y-2">
            <Label>Son Tarih</Label>
            <Input
              type="date"
              data-testid="form-due-date"
              value={form.due_date || ""}
              onChange={(e) => set("due_date")(e.target.value)}
            />
          </div>

          <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                data-testid="form-more-toggle"
                className="flex w-full items-center justify-between rounded-xl border border-border px-4 py-3 text-sm font-medium text-foreground transition-colors duration-150 hover:bg-muted"
              >
                Diğer Bilgiler
                <ChevronDown className={cn("h-4 w-4 transition-transform duration-150", moreOpen && "rotate-180")} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Kayıt Türü</Label>
                  <Select value={form.record_type} onValueChange={set("record_type")}>
                    <SelectTrigger data-testid="form-record-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(RECORD_TYPES[form.section] || []).map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Evrak Tarihi</Label>
                  <Input
                    type="date"
                    data-testid="form-doc-date"
                    value={form.doc_date || ""}
                    onChange={(e) => set("doc_date")(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border p-4">
                <div>
                  <p className="text-sm font-medium">Süre kontrolü gerekli</p>
                  <p className="text-xs text-muted-foreground">Son tarih belirlenemiyorsa işaretleyin.</p>
                </div>
                <Switch
                  data-testid="form-deadline-check"
                  checked={!!form.deadline_needs_check}
                  onCheckedChange={set("deadline_needs_check")}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Öncelik</Label>
                  <Select value={form.priority} onValueChange={set("priority")}>
                    <SelectTrigger data-testid="form-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Durum</Label>
                  <Select value={form.status} onValueChange={set("status")} disabled={editing}>
                    <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.filter((s) => s !== "Tamamlandı").map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sorumlu</Label>
                  <Select value={form.assignee || ""} onValueChange={set("assignee")}>
                    <SelectTrigger data-testid="form-assignee"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ASSIGNEES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.drive_link && (
                <p className="truncate rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground">
                  Drive evrakı bağlı: {form.drive_link}
                </p>
              )}

              <div className="space-y-2">
                <Label>Hedef Klasör Bağlantısı</Label>
                <Input
                  data-testid="form-target-folder"
                  value={form.target_folder_link || ""}
                  onChange={(e) => set("target_folder_link")(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">
            Vazgeç
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="form-submit"
            className="bg-crimson text-white hover:bg-crimson/90"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
